import { z } from 'zod';
import {
  INTEGRATION_AUTH_MODES,
  INTEGRATION_PROVIDERS,
  KNOWLEDGE_SYNC_PROVIDERS,
  SCOPE_TYPE,
  SYNC_STATUS,
  INGESTION_LINK_STATUS,
} from './knowledge-sync.constants';

// --- Provider schemas ---

export const IntegrationProviderSchema = z.enum([
  INTEGRATION_PROVIDERS.LINEAR,
  INTEGRATION_PROVIDERS.JIRA,
  INTEGRATION_PROVIDERS.SERVICENOW,
  INTEGRATION_PROVIDERS.CONFLUENCE,
  INTEGRATION_PROVIDERS.NOTION,
  INTEGRATION_PROVIDERS.GOOGLE_DRIVE,
  INTEGRATION_PROVIDERS.BOX,
  INTEGRATION_PROVIDERS.WIZ,
  INTEGRATION_PROVIDERS.HACKERONE,
  INTEGRATION_PROVIDERS.XPANSE,
  INTEGRATION_PROVIDERS.CROWDSTRIKE,
  INTEGRATION_PROVIDERS.BUGCROWD,
  INTEGRATION_PROVIDERS.UPWIND,
]);

export const KnowledgeSyncProviderSchema = z.enum([
  KNOWLEDGE_SYNC_PROVIDERS.CONFLUENCE,
  KNOWLEDGE_SYNC_PROVIDERS.NOTION,
  KNOWLEDGE_SYNC_PROVIDERS.GOOGLE_DRIVE,
  KNOWLEDGE_SYNC_PROVIDERS.BOX,
]);

export const SyncStatusSchema = z.enum([
  SYNC_STATUS.INITIALIZING,
  SYNC_STATUS.ACTIVE,
  SYNC_STATUS.PROCESSING,
  SYNC_STATUS.IDLE,
  SYNC_STATUS.ERRORED,
  SYNC_STATUS.DISABLED,
]);

export const ScopeTypeSchema = z.enum([SCOPE_TYPE.CONTAINER, SCOPE_TYPE.ITEM]);

export const IngestionLinkStatusSchema = z.enum([
  INGESTION_LINK_STATUS.ACTIVE,
  INGESTION_LINK_STATUS.SUPERSEDED,
  INGESTION_LINK_STATUS.PENDING_DELETE,
  INGESTION_LINK_STATUS.DELETED,
]);

// --- Container / item schemas (for scope picker) ---

export const KnowledgeResourceSchema = z.object({
  id: z.string().describe('Provider-specific resource ID'),
  name: z.string().describe('Human-readable resource name'),
  url: z.string().optional().describe('Link to the resource in the provider'),
});

export const KnowledgeContainerSchema = z.object({
  id: z
    .string()
    .describe('Provider-specific container ID (e.g. Confluence space key)'),
  name: z.string().describe('Human-readable name'),
  type: z
    .string()
    .optional()
    .describe('Container type (e.g. global, personal)'),
  url: z.string().optional().describe('Link to the container in the provider'),
  itemCount: z.number().optional().describe('Number of items in the container'),
  hasChildren: z
    .boolean()
    .optional()
    .describe('Whether this container has sub-containers (nested folders)'),
});

export const KnowledgeItemSchema = z.object({
  id: z.string().describe('Provider-specific item ID'),
  name: z.string().describe('Item title or filename'),
  mimeType: z.string().optional(),
  url: z.string().optional(),
  parentContainerId: z.string().optional().describe('ID of parent container'),
  updatedAt: z.string().optional().describe('ISO date of last update'),
});

// --- Mapping schemas ---

export const CreateMappingRequestSchema = z.object({
  applicationId: z.string().describe('Application to map content to'),
  provider: KnowledgeSyncProviderSchema,
  scopes: z
    .array(
      z.object({
        scopeType: ScopeTypeSchema,
        scopeExternalId: z.string().describe('Provider-specific scope ID'),
        scopeName: z.string().optional().describe('Human-readable scope name'),
      }),
    )
    .min(1, 'At least one scope is required'),
});

export const MappingResponseSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  sourceId: z.string(),
  scopeType: ScopeTypeSchema,
  scopeExternalId: z.string(),
  scopeName: z.string().nullable(),
  createdAt: z.string(),
});

export const DeleteMappingRequestSchema = z.object({
  mappingId: z.string(),
});

// --- Source status schema ---

export const ContentSourceStatusSchema = z.object({
  id: z.string(),
  provider: KnowledgeSyncProviderSchema,
  syncStatus: SyncStatusSchema,
  lastSyncedAt: z.string().nullable(),
  itemCount: z.number(),
  errorMessage: z.string().nullable(),
});

// --- App knowledge summary ---

export const AppKnowledgeSummarySchema = z.object({
  sourceId: z.string(),
  provider: KnowledgeSyncProviderSchema,
  syncStatus: SyncStatusSchema,
  errorMessage: z.string().nullable(),
  mappings: z.array(MappingResponseSchema),
  totalItems: z.number(),
  activeIngestionLinks: z.number(),
  lastSyncedAt: z.string().nullable(),
  processedItemCount: z.number(),
  dispatchedItemCount: z.number(),
});

// --- Connection status schemas ---

export const CredentialStatusSchema = z.enum([
  'VALID',
  'EXPIRED',
  'REVOKED',
  'NOT_CONNECTED',
]);

export const IntegrationAuthModeSchema = z.enum([
  INTEGRATION_AUTH_MODES.OAUTH,
  INTEGRATION_AUTH_MODES.CCG,
]);

export const ConnectionStatusSchema = z.object({
  provider: KnowledgeSyncProviderSchema,
  status: CredentialStatusSchema,
  providerUserId: z.string().nullable(),
  authMode: IntegrationAuthModeSchema.optional().describe(
    'How the credential was obtained. Only reported for providers that support more than one auth mode.',
  ),
});

export const BoxCcgConnectBodySchema = z.object({
  clientId: z
    .string()
    .trim()
    .min(1)
    .max(256)
    .describe('Client ID of the customer-owned Box Platform App'),
  clientSecret: z
    .string()
    .trim()
    .min(1)
    .max(256)
    .describe('Client secret of the customer-owned Box Platform App'),
  enterpriseId: z
    .string()
    .trim()
    .min(1)
    .max(32)
    .regex(/^\d+$/, 'Box enterprise ID must be numeric')
    .describe('Box enterprise ID that the app is authorized against'),
});

// --- Ingestion link schema ---

export const IngestionLinkSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  itemId: z.string(),
  s3Key: z.string(),
  contentHash: z.string().nullable(),
  status: IngestionLinkStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
